import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Raven from 'raven-js';
import styled, { StyleSheetManager } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ASPECT_RATIO = 16 / 9;
const HANDLE_HEIGHT = 28;
const INITIAL_WIDTH = 500;
const INITIAL_TOP = 100;
const INITIAL_LEFT = 100;
const MIN_WIDTH = 200;
const FLOATING_LAYOUT_KEY = 'hs-video-player-floating-layout';
const getSavedLayout = () => {
  try {
    const saved = sessionStorage.getItem(FLOATING_LAYOUT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (_unused) {
    return null;
  }
};
function DragHandleIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 32",
    width: "16",
    height: "16",
    fill: "currentColor",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("circle", {
      cx: "10.14",
      cy: "4.14",
      r: "2.29"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "10.14",
      cy: "15.57",
      r: "2.29"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "10.14",
      cy: "27",
      r: "2.29"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "21.57",
      cy: "4.14",
      r: "2.29"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "21.57",
      cy: "15.57",
      r: "2.29"
    }), /*#__PURE__*/_jsx("circle", {
      cx: "21.57",
      cy: "27",
      r: "2.29"
    })]
  });
}
function CloseIcon() {
  return /*#__PURE__*/_jsxs("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 32",
    width: "12",
    height: "12",
    fill: "currentColor",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M27.86 29c-.29 0-.58-.11-.81-.33L4.19 5.81a1.14 1.14 0 0 1 0-1.62 1.14 1.14 0 0 1 1.62 0l22.86 22.86a1.14 1.14 0 0 1-.81 1.95"
    }), /*#__PURE__*/_jsx("path", {
      d: "M5 29c-.29 0-.58-.11-.81-.33a1.14 1.14 0 0 1 0-1.62L27.05 4.19a1.14 1.14 0 0 1 1.62 0c.45.45.45 1.17 0 1.62L5.81 28.67c-.22.22-.52.33-.81.33"
    })]
  });
}
const DragHandleContainer = styled.div.withConfig({
  displayName: "Floating__DragHandleContainer"
})(["height:", "px;background:rgba(0,0,0,0.5);color:#fff;display:flex;align-items:center;justify-content:center;cursor:grab;border-radius:4px 4px 0 0;position:relative;&:active{cursor:grabbing;}"], HANDLE_HEIGHT);
const CloseBtn = styled.button.withConfig({
  displayName: "Floating__CloseBtn"
})(["position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;color:#fff;cursor:pointer;padding:4px;display:flex;align-items:center;justify-content:center;border-radius:2px;&:hover{background:rgba(255,255,255,0.2);}"]);
const ResizeEdge = styled.div.withConfig({
  displayName: "Floating__ResizeEdge"
})(["position:absolute;width:16px;height:16px;z-index:123;", ""], ({
  $direction
}) => {
  switch ($direction) {
    case 'top-left':
      return 'top: 0; left: 0; cursor: nw-resize;';
    case 'top-right':
      return 'top: 0; right: 0; cursor: ne-resize;';
    case 'bottom-left':
      return 'bottom: 0; left: 0; cursor: sw-resize;';
    case 'bottom-right':
      return 'bottom: 0; right: 0; cursor: se-resize;';
    default:
      return '';
  }
});
const DragOverlay = styled.div.withConfig({
  displayName: "Floating__DragOverlay"
})(["position:fixed;inset:0;z-index:9999;cursor:inherit;"]);
export function Floating({
  children,
  onClose,
  targetWindow
}) {
  const targetDOM = useMemo(() => {
    if (targetWindow) return targetWindow;
    try {
      var _window$top;
      return (_window$top = window.top) !== null && _window$top !== void 0 ? _window$top : window;
    } catch (_unused2) {
      return window;
    }
  }, [targetWindow]);
  const [layout, setLayout] = useState(() => {
    var _saved$top, _saved$left, _saved$width;
    const saved = getSavedLayout();
    return {
      position: {
        top: (_saved$top = saved === null || saved === void 0 ? void 0 : saved.top) !== null && _saved$top !== void 0 ? _saved$top : INITIAL_TOP,
        left: (_saved$left = saved === null || saved === void 0 ? void 0 : saved.left) !== null && _saved$left !== void 0 ? _saved$left : INITIAL_LEFT
      },
      width: (_saved$width = saved === null || saved === void 0 ? void 0 : saved.width) !== null && _saved$width !== void 0 ? _saved$width : INITIAL_WIDTH
    };
  });
  const {
    position,
    width
  } = layout;
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  useEffect(() => {
    try {
      sessionStorage.setItem(FLOATING_LAYOUT_KEY, JSON.stringify({
        top: position.top,
        left: position.left,
        width
      }));
    } catch (e) {
      Raven.captureException(e instanceof Error ? e : new Error(String(e)));
    }
  }, [position, width]);
  const dragStartRef = useRef(null);
  const resizeStartRef = useRef(null);
  const portalTargetRef = useRef(null);
  const styleLinkRef = useRef(null);
  if (!portalTargetRef.current) {
    const existingLink = document.querySelector('link[rel="stylesheet"][href*="project.css"]');
    if (existingLink) {
      const link = targetDOM.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = existingLink.href;
      targetDOM.document.head.appendChild(link);
      styleLinkRef.current = link;
    }
    const contentEl = targetDOM.document.createElement('div');
    targetDOM.document.body.appendChild(contentEl);
    portalTargetRef.current = contentEl;
  }
  const portalTarget = portalTargetRef.current;
  useEffect(() => {
    return () => {
      var _portalTargetRef$curr, _styleLinkRef$current;
      (_portalTargetRef$curr = portalTargetRef.current) === null || _portalTargetRef$curr === void 0 || _portalTargetRef$curr.remove();
      (_styleLinkRef$current = styleLinkRef.current) === null || _styleLinkRef$current === void 0 || _styleLinkRef$current.remove();
    };
  }, []);
  useEffect(() => {
    // styled-components uses instanceof checks (e.g. el instanceof HTMLElement)
    // which fail cross-frame because each browsing context has its own built-in
    // constructors. Since this component runs in a child frame but portals into
    // the parent document, we patch the child frame's globals with the parent's
    // constructors so those checks pass for nodes owned by the parent frame.
    if (targetDOM === window) {
      return;
    }
    const originalHTMLElement = window.HTMLElement;
    const originalElement = window.Element;
    const originalNode = window.Node;
    window.HTMLElement = targetDOM.HTMLElement;
    window.Element = targetDOM.Element;
    window.Node = targetDOM.Node;
    return () => {
      if (window.HTMLElement === targetDOM.HTMLElement) {
        window.HTMLElement = originalHTMLElement;
      }
      if (window.Element === targetDOM.Element) {
        window.Element = originalElement;
      }
      if (window.Node === targetDOM.Node) {
        window.Node = originalNode;
      }
    };
  }, [targetDOM]);
  const handleDragMouseDown = useCallback(e => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      top: position.top,
      left: position.left
    };
  }, [position]);
  const handleResizeMouseDown = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      mouseX: e.clientX,
      width,
      top: position.top,
      left: position.left,
      direction
    };
  }, [width, position]);
  useEffect(() => {
    const doc = targetDOM.document;
    const onMouseMove = e => {
      if (dragStartRef.current) {
        const dx = e.clientX - dragStartRef.current.mouseX;
        const dy = e.clientY - dragStartRef.current.mouseY;
        const newTop = Math.min(Math.max(0, dragStartRef.current.top + dy), targetDOM.innerHeight - (HANDLE_HEIGHT + width / ASPECT_RATIO));
        const newLeft = Math.min(Math.max(0, dragStartRef.current.left + dx), targetDOM.innerWidth - width);
        setLayout(prev => Object.assign({}, prev, {
          position: {
            top: newTop,
            left: newLeft
          }
        }));
      }
      if (resizeStartRef.current) {
        const {
          mouseX,
          width: startWidth,
          top: startTop,
          left: startLeft,
          direction
        } = resizeStartRef.current;
        const dx = e.clientX - mouseX;
        let newWidth = startWidth;
        let newTop = startTop;
        let newLeft = startLeft;
        switch (direction) {
          case 'bottom-right':
            newWidth = Math.max(MIN_WIDTH, startWidth + dx);
            break;
          case 'bottom-left':
            newWidth = Math.max(MIN_WIDTH, startWidth - dx);
            newLeft = startLeft + (startWidth - newWidth);
            break;
          case 'top-right':
            newWidth = Math.max(MIN_WIDTH, startWidth + dx);
            newTop = startTop - (newWidth - startWidth) / ASPECT_RATIO;
            break;
          case 'top-left':
            newWidth = Math.max(MIN_WIDTH, startWidth - dx);
            newLeft = startLeft + (startWidth - newWidth);
            newTop = startTop + (startWidth - newWidth) / ASPECT_RATIO;
            break;
          default:
            break;
        }
        const maxWidthFromRight = targetDOM.innerWidth - newLeft;
        const maxWidthFromBottom = (targetDOM.innerHeight - newTop - HANDLE_HEIGHT) * ASPECT_RATIO;
        newWidth = Math.min(newWidth, maxWidthFromRight, maxWidthFromBottom);
        setLayout(prev => Object.assign({}, prev, {
          position: {
            top: Math.max(0, newTop),
            left: Math.max(0, newLeft)
          },
          width: newWidth
        }));
      }
    };
    const onMouseUp = () => {
      dragStartRef.current = null;
      resizeStartRef.current = null;
      setIsDragging(false);
      setIsResizing(false);
    };
    doc.addEventListener('mousemove', onMouseMove);
    doc.addEventListener('mouseup', onMouseUp);
    return () => {
      doc.removeEventListener('mousemove', onMouseMove);
      doc.removeEventListener('mouseup', onMouseUp);
    };
  }, [targetDOM.document, targetDOM.innerHeight, targetDOM.innerWidth, width]);
  const videoHeight = width / ASPECT_RATIO;
  if (!portalTarget) return null;
  return /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(StyleSheetManager, {
    target: targetDOM.document.head,
    children: /*#__PURE__*/_jsxs("div", {
      id: "floating-draggable-container",
      style: {
        position: 'fixed',
        zIndex: 1234,
        top: position.top,
        left: position.left,
        width,
        display: 'flex',
        flexDirection: 'column'
      },
      "data-test-id": "floating-draggable-container",
      children: [(isDragging || isResizing) && /*#__PURE__*/_jsx(DragOverlay, {}), /*#__PURE__*/_jsxs(DragHandleContainer, {
        onMouseDown: handleDragMouseDown,
        children: [/*#__PURE__*/_jsx(DragHandleIcon, {}), onClose && /*#__PURE__*/_jsx(CloseBtn, {
          onMouseDown: e => e.stopPropagation(),
          onClick: onClose,
          "aria-label": "close",
          children: /*#__PURE__*/_jsx(CloseIcon, {})
        })]
      }), /*#__PURE__*/_jsxs("div", {
        style: {
          position: 'relative',
          width: '100%',
          height: videoHeight
        },
        children: [['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(direction => /*#__PURE__*/_jsx(ResizeEdge, {
          $direction: direction,
          onMouseDown: e => handleResizeMouseDown(e, direction)
        }, direction)), children]
      })]
    })
  }), portalTarget);
}